import { ElementType, FC, PropsWithChildren, ReactNode } from 'react';
import { Box, BoxProps, Grid, Stack, StackProps, Typography } from '@mui/material';

import { ContentIcon } from '@components/base';
import { NewTag } from '@components/content';
import { ContentType } from '@models/Content';

import { PageContentPadding, spacing } from './utils';

export type PageContentTemplateProps = PropsWithChildren<{
  actions?: ReactNode;
  addNewButton?: ReactNode;
  ContentBoxProps?: BoxProps;
  contentType?: ContentType;
  dense?: boolean;
  isNew?: boolean;
  pageTitle?: ReactNode;
  decoratePageTitle?: (x: ReactNode) => ReactNode;
  subheading?: ReactNode;
  WrapperProps?: StackProps;
}>;

export const ContentBox: FC<BoxProps & { component?: ElementType }> = ({
  width = 1,
  p = PageContentPadding,
  ...rest
}) => <Box p={p} width={width} {...rest} />;

export const PageContentTemplate: FC<PageContentTemplateProps> = ({
  actions,
  addNewButton,
  children,
  ContentBoxProps: { p: contentBoxPadding = 0, height: contentHeight = 1, ...contentBoxProps } = {},
  contentType,
  dense,
  isNew,
  pageTitle,
  decoratePageTitle,
  subheading,
  WrapperProps: {
    spacing: wrapperSpacing = spacing,
    alignItems: wrapperAlignItems = 'flex-start',
    width: wrapperWidth = 1,
    height: wrapperHeight = 1,
    ...wrapperProps
  } = {},
}) => (
  <Stack
    alignItems={wrapperAlignItems}
    height={wrapperHeight}
    p={dense ? undefined : PageContentPadding}
    spacing={wrapperSpacing}
    width={wrapperWidth}
    {...wrapperProps}
  >
    <Box width={1} {...(dense ? { pt: PageContentPadding, px: PageContentPadding } : {})}>
      <Grid container spacing={1}>
        <Grid size='grow'>
          <Stack alignItems='center' direction='row' gap={1}>
            {(decoratePageTitle ?? (x => x))(
              <Typography alignItems='center' display='flex' gap={spacing} variant='h1'>
                {contentType && (
                  <ContentIcon contentType={contentType} fontSize='inherit' hideTooltip />
                )}
                {pageTitle}
              </Typography>
            )}

            {isNew && <NewTag size='small' />}
          </Stack>

          <Typography color='text.secondary' variant='subtitle1'>
            {subheading}
          </Typography>
        </Grid>

        {actions && (
          <Grid display='flex' height={1} justifyContent='flex-end'>
            {actions}
          </Grid>
        )}
      </Grid>
    </Box>

    {addNewButton}

    <ContentBox height={contentHeight} p={contentBoxPadding} {...contentBoxProps}>
      {children}
    </ContentBox>
  </Stack>
);
